// src/pages/buyer/InternationalCheckoutPage.jsx
// Phase 6 — M6: International checkout with live tax/duty estimation,
//              Stripe card and PayPal wallet payment flows

import { useEffect, useState, useCallback } from 'react'

const PINK  = '#FF3D7F'
const ORG   = '#FF8500'
const DARK  = '#1A1A2E'
const GREY  = '#9CA3AF'
const GR_LT = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

const GATEWAYS = [
  { id: 'stripe', label: 'Credit / Debit Card', sub: 'Visa, Mastercard, Amex — powered by Stripe', icon: '💳' },
  { id: 'paypal', label: 'PayPal',               sub: 'PayPal wallet or Pay Later',                 icon: '🅿️' },
  { id: 'razorpay', label: 'Razorpay (India)',   sub: 'UPI, Net Banking, Cards (INR only)',          icon: '₹' },
]

function Row({ label, value, bold, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #F3F4F6' }}>
      <span style={{ color: GREY, fontSize: '0.87rem' }}>{label}</span>
      <span style={{ color: color || DARK, fontWeight: bold ? 700 : 500, fontSize: '0.87rem' }}>{value}</span>
    </div>
  )
}

function StepBadge({ step, current, label }) {
  const done    = current > step
  const active  = current === step
  const bgColor = done ? '#10B981' : active ? PINK : '#E5E7EB'
  const color   = done || active ? '#fff' : GREY
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: bgColor, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 800, flexShrink: 0 }}>
        {done ? '✓' : step}
      </div>
      <span style={{ fontSize: '0.83rem', fontWeight: active ? 700 : 500, color: active ? DARK : GREY }}>{label}</span>
    </div>
  )
}

export default function InternationalCheckoutPage() {
  // Simulated cart values — in production these come from cart store / route params
  const [orderId]     = useState(null)       // set after order creation
  const [subtotalInr] = useState(15000)      // example ₹15,000 order
  const [shippingInr] = useState(2500)       // example ₹2,500 shipping

  const [step, setStep]                 = useState(1)
  const [countries, setCountries]       = useState([])
  const [locale, setLocale]             = useState(null)
  const [selectedCountry, setSelectedCountry] = useState('US')
  const [selectedCurrency, setSelectedCurrency] = useState('USD')
  const [breakdown, setBreakdown]       = useState(null)
  const [loadingBreakdown, setLoadingBreakdown] = useState(false)
  const [gateway, setGateway]           = useState('stripe')
  const [payProcessing, setPayProcessing] = useState(false)
  const [payResult, setPayResult]       = useState(null)  // success | error
  const [payError, setPayError]         = useState('')

  // -- Load countries and initial locale
  useEffect(() => {
    Promise.all([
      api('/international/countries'),
      api('/international/locale'),
    ]).then(([cRes, lRes]) => {
      const list = cRes.data ?? cRes
      setCountries(Array.isArray(list) ? list : [])
      const loc  = lRes.data ?? lRes
      setLocale(loc)
      if (loc?.country_code && loc.country_code !== 'IN') {
        setSelectedCountry(loc.country_code)
        setSelectedCurrency(loc.currency)
      }
    })
  }, [])

  // -- Recompute breakdown whenever country/currency changes
  const fetchBreakdown = useCallback(() => {
    if (!selectedCountry || !selectedCurrency) return
    setLoadingBreakdown(true)
    api('/buyer/international/tax-estimate', {
      method: 'POST',
      body: JSON.stringify({
        order_value_inr:     subtotalInr,
        destination_country: selectedCountry,
        display_currency:    selectedCurrency,
        shipping_inr:        shippingInr,
        hs_code:             '5208',
      }),
    }).then(r => {
      setBreakdown(r.data ?? r)
      setLoadingBreakdown(false)
    })
  }, [selectedCountry, selectedCurrency, subtotalInr, shippingInr])

  useEffect(() => { fetchBreakdown() }, [fetchBreakdown])

  // -- When a country is picked, set its default currency
  const pickCountry = (code) => {
    const found = countries.find(c => c.country_code === code)
    setSelectedCountry(code)
    if (found?.currency_code) setSelectedCurrency(found.currency_code)
  }

  const handlePay = async () => {
    setPayProcessing(true)
    setPayError('')

    if (gateway === 'stripe') {
      // In production: create a real order first, then create intent
      const intentRes = await api('/buyer/international/stripe/create-intent', {
        method: 'POST',
        body: JSON.stringify({ order_id: orderId ?? 1, currency: selectedCurrency.toLowerCase() }),
      }).catch(() => null)

      if (!intentRes || intentRes.status === 'error') {
        setPayError(intentRes?.message ?? 'Could not initialise Stripe payment. Check STRIPE_PUBLISHABLE_KEY.')
        setPayProcessing(false)
        return
      }
      // In a real implementation the Stripe.js SDK would open 3DS UI here
      // For demo, simulate success:
      setPayResult('success')
    } else if (gateway === 'paypal') {
      const ppRes = await api('/buyer/international/paypal/create-order', {
        method: 'POST',
        body: JSON.stringify({ order_id: orderId ?? 1, currency: selectedCurrency }),
      }).catch(() => null)

      if (ppRes?.data?.approval_url) {
        // Redirect to PayPal approval page
        window.location.href = ppRes.data.approval_url
        return
      }
      setPayError(ppRes?.message ?? 'Could not initialise PayPal. Check PAYPAL_CLIENT_ID.')
    } else {
      setPayResult('success') // Razorpay handled separately
    }

    setPayProcessing(false)
  }

  const sym  = breakdown?.currency ? (breakdown.currency === 'INR' ? '₹' : breakdown.currency + ' ') : '$'
  const fmt  = (n) => sym + parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  if (payResult === 'success') return (
    <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center', padding: 32, background: '#fff', borderRadius: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>🎉</div>
      <h2 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, color: DARK, margin: '0 0 8px' }}>Payment Successful!</h2>
      <p style={{ color: GREY, marginBottom: 24 }}>Your international order has been placed. You'll receive a confirmation email shortly.</p>
      <button onClick={() => setPayResult(null)} style={{ padding: '11px 28px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
        Place Another Order
      </button>
    </div>
  )

  return (
    <div style={{ maxWidth: 1060, margin: '0 auto', padding: '0 0 56px' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.85rem', color: DARK, margin: 0 }}>
          International Checkout
        </h1>
        <p style={{ color: GREY, margin: '4px 0 0', fontSize: '0.88rem' }}>
          Order to {countries.find(c => c.country_code === selectedCountry)?.country_name ?? selectedCountry} — taxes &amp; duties calculated in real time
        </p>
      </div>

      {/* Step progress */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 28, flexWrap: 'wrap' }}>
        {[[1,'Destination'],[2,'Cost Breakdown'],[3,'Payment']].map(([s, l]) => (
          <StepBadge key={s} step={s} current={step} label={l} />
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 24, alignItems: 'start' }}>

        {/* ── Left panel ── */}
        <div>

          {/* Step 1: Destination */}
          {step === 1 && (
            <div style={{ background: '#fff', borderRadius: 18, padding: 28, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1.1rem', color: DARK, marginTop: 0, marginBottom: 20 }}>
                Ship to
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: GREY, marginBottom: 6 }}>Destination Country</label>
                  <select
                    value={selectedCountry}
                    onChange={e => pickCountry(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', background: '#fff' }}
                  >
                    {countries.map(c => (
                      <option key={c.country_code} value={c.country_code}>{c.country_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: GREY, marginBottom: 6 }}>Display Currency</label>
                  <select
                    value={selectedCurrency}
                    onChange={e => setSelectedCurrency(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', background: '#fff' }}
                  >
                    {['USD','EUR','GBP','AED','SGD','JPY','AUD','CAD','INR'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              {locale && (
                <div style={{ padding: '12px 16px', background: '#F0FDF4', borderRadius: 10, fontSize: '0.83rem', color: '#166534', marginBottom: 20 }}>
                  🌍 Detected your region: <strong>{locale.country_name}</strong> — showing prices in <strong>{locale.currency}</strong>
                </div>
              )}
              <button onClick={() => setStep(2)} style={{ padding: '11px 28px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                Continue →
              </button>
            </div>
          )}

          {/* Step 2: Cost Breakdown */}
          {step === 2 && (
            <div style={{ background: '#fff', borderRadius: 18, padding: 28, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1.1rem', color: DARK, marginTop: 0, marginBottom: 20 }}>
                Full Cost Breakdown
              </h3>
              {loadingBreakdown ? (
                <div style={{ color: GREY, padding: 24, textAlign: 'center' }}>Calculating taxes &amp; duties…</div>
              ) : breakdown ? (
                <div>
                  <Row label="Subtotal"                     value={fmt(breakdown.subtotal)} />
                  <Row label="Shipping"                     value={fmt(breakdown.shipping)} />
                  {breakdown.tax?.tax_amount_foreign > 0 && (
                    <Row label={`${breakdown.tax.tax_name ?? 'Tax'} (${breakdown.tax.rate_pct}%)`}
                         value={fmt(breakdown.tax.tax_amount_foreign)}
                         color={breakdown.tax.below_threshold ? '#10B981' : DARK} />
                  )}
                  {breakdown.tax?.below_threshold && (
                    <div style={{ fontSize: '0.77rem', color: '#10B981', padding: '4px 0 8px' }}>
                      ✓ Below de minimis threshold ({sym}{breakdown.tax.threshold_foreign}) — no {breakdown.tax.tax_name} applied
                    </div>
                  )}
                  {breakdown.duty?.duty_amount > 0 && (
                    <Row label={`Estimated Customs Duty (${breakdown.duty.duty_rate_pct}%)`} value={fmt(breakdown.duty.duty_amount)} />
                  )}
                  <div style={{ borderTop: '2px solid #E5E7EB', marginTop: 8, paddingTop: 12 }}>
                    <Row label="Estimated Total" value={fmt(breakdown.total)} bold color={PINK} />
                  </div>
                  {breakdown.disclaimer && (
                    <p style={{ fontSize: '0.75rem', color: GREY, marginTop: 12, lineHeight: 1.5 }}>{breakdown.disclaimer}</p>
                  )}
                  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                    <button onClick={() => setStep(1)} style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>← Back</button>
                    <button onClick={() => setStep(3)} style={{ padding: '10px 28px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Proceed to Payment →</button>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#EF4444' }}>Failed to load cost breakdown. Please try again.</div>
              )}
            </div>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <div style={{ background: '#fff', borderRadius: 18, padding: 28, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
              <h3 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1.1rem', color: DARK, marginTop: 0, marginBottom: 20 }}>
                Choose Payment Method
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                {GATEWAYS.map(g => (
                  <label key={g.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderRadius: 12, border: `2px solid ${gateway === g.id ? PINK : '#E5E7EB'}`, background: gateway === g.id ? '#FFF1F5' : '#fff', cursor: 'pointer' }}
                  >
                    <input type="radio" value={g.id} checked={gateway === g.id} onChange={() => setGateway(g.id)} style={{ accentColor: PINK }} />
                    <span style={{ fontSize: '1.4rem' }}>{g.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, color: DARK, fontSize: '0.9rem' }}>{g.label}</div>
                      <div style={{ color: GREY, fontSize: '0.78rem' }}>{g.sub}</div>
                    </div>
                  </label>
                ))}
              </div>

              {payError && (
                <div style={{ background: '#FEF2F2', color: '#991B1B', padding: '10px 14px', borderRadius: 10, fontSize: '0.84rem', marginBottom: 16 }}>
                  ⚠️ {payError}
                </div>
              )}

              {gateway === 'stripe' && (
                <div style={{ background: '#F0FDF4', borderRadius: 10, padding: '12px 16px', fontSize: '0.83rem', color: '#166534', marginBottom: 16 }}>
                  🔒 Your card details are securely collected by Stripe and never touch our servers. 3D Secure authentication will apply where required.
                </div>
              )}
              {gateway === 'paypal' && (
                <div style={{ background: '#EFF6FF', borderRadius: 10, padding: '12px 16px', fontSize: '0.83rem', color: '#1E40AF', marginBottom: 16 }}>
                  You'll be redirected to PayPal to complete your payment securely.
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={() => setStep(2)} style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>← Back</button>
                <button
                  onClick={handlePay}
                  disabled={payProcessing}
                  style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', background: payProcessing ? '#D1D5DB' : PINK, color: '#fff', fontWeight: 800, cursor: payProcessing ? 'default' : 'pointer', fontSize: '0.95rem' }}
                >
                  {payProcessing ? 'Processing…' : `Pay ${breakdown ? fmt(breakdown.total) : '…'}`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Order Summary Sidebar ── */}
        <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 6px rgba(0,0,0,0.08)', position: 'sticky', top: 24 }}>
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: '1rem', color: DARK, marginTop: 0, marginBottom: 16 }}>Order Summary</h3>

          {/* Mock items */}
          {[
            { name: 'Kanchipuram Silk — Red', qty: '25m', price: '₹8,500' },
            { name: 'Cotton Muslin — Ivory', qty: '40m', price: '₹6,500' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <div>
                <div style={{ fontWeight: 600, color: DARK, fontSize: '0.84rem' }}>{item.name}</div>
                <div style={{ color: GREY, fontSize: '0.75rem' }}>{item.qty}</div>
              </div>
              <div style={{ fontWeight: 700, color: DARK, fontSize: '0.84rem', whiteSpace: 'nowrap' }}>{item.price}</div>
            </div>
          ))}

          <div style={{ marginTop: 14 }}>
            <Row label="Subtotal (INR)"  value={`₹${subtotalInr.toLocaleString('en-IN')}`} />
            <Row label="Shipping (INR)"  value={`₹${shippingInr.toLocaleString('en-IN')}`} />
            {breakdown && selectedCurrency !== 'INR' && (
              <>
                <Row label={`Exchange Rate`}   value={`1 INR = ${parseFloat(breakdown.exchange_rate ?? 0).toFixed(4)} ${selectedCurrency}`} />
                <Row label={`Total (${selectedCurrency})`} value={fmt(breakdown.total)} bold color={PINK} />
              </>
            )}
          </div>

          <div style={{ marginTop: 16, padding: '12px 14px', background: GR_LT, borderRadius: 10, fontSize: '0.78rem', color: GREY, lineHeight: 1.5 }}>
            🌍 Shipping from Surat, Gujarat, India<br />
            📦 Estimated delivery: 7–14 business days<br />
            ✈️ via DHL Express / Aramex
          </div>
        </div>
      </div>
    </div>
  )
}
